import { useState } from "react";
import { toast } from "sonner";
import { Home, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { SellerAuthGate } from "./SellerAuthGate";
import {
  US_STATES, CONDITION, PublicShell, Field, SubmissionResult, inputCls,
  RoleAttestation, TimelineField, ReviewProcessNotice, AddressVisibilityNote,
} from "./_shared";

export default function SubmitMultifamily() {
  const [f, setF] = useState({
    property_address: "", state: "", county: "", units: "", asking_price: "",
    annual_gross_rents: "", occupancy: "", cap_rate: "", year_built: "", condition: "",
    seller_financing_available: false, seller_financing_terms: "",
    contact_name: "", contact_role: "owner", contact_phone: "",
    listing_agreement_accepted: false,
    owner_attestation: false, representative_attestation: false,
    timeline: "exploring",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.listing_agreement_accepted) { toast.error("Please confirm the listing agreement."); return; }
    if (f.contact_role === "owner" && !f.owner_attestation) { toast.error("Please confirm the owner attestation."); return; }
    if ((f.contact_role === "broker" || f.contact_role === "other") && !f.representative_attestation) { toast.error("Please confirm the authorized-representative attestation."); return; }
    setBusy(true);
    try {
      const payload = {
        ...f,
        units: parseInt(f.units || "0", 10) || 0,
        asking_price: Number(f.asking_price) || 0,
        annual_gross_rents: f.annual_gross_rents === "" ? null : Number(f.annual_gross_rents),
        occupancy: f.occupancy === "" ? null : Number(f.occupancy),
        cap_rate: f.cap_rate === "" ? null : Number(f.cap_rate),
        year_built: f.year_built === "" ? null : parseInt(f.year_built, 10) || null,
      };
      const r = await api.post("/listings/submit/multifamily", payload);
      if (r.data?.duplicate) toast.warning("This property appears to already be in our system.");
      else toast.success("Listing received");
      setResult({
        oid: r.data?.oid,
        title: f.property_address,
        address: f.property_address,
        confirmationEmailSent: r.data?.confirmation_email_sent,
      });
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Submission failed.");
    } finally { setBusy(false); }
  };

  if (result) {
    return (
      <PublicShell seoRoute="/submit-multifamily" title="Submit a Multifamily Listing" testid="submit-multifamily-page">
        <SubmissionResult
          testid="thank-you-multifamily"
          title={result.title}
          address={result.address}
          oid={result.oid}
          confirmationEmailSent={result.confirmationEmailSent}
        />
      </PublicShell>
    );
  }

  return (
    <PublicShell
      seoRoute="/submit-multifamily"
      title="Submit a Multifamily Listing to Acquigine"
      subtitle="Apartment buildings, duplexes through large complexes. Submit to a network of acquirers exploring seller-financed, master-lease, and subject-to structures. Every submission is reviewed by our team before it becomes visible to any acquirer, and may be declined."
      testid="submit-multifamily-page"
    >
      <SellerAuthGate>
        <form onSubmit={submit} className="ae-card p-6 space-y-5">
          <div className="flex items-center gap-3 pb-4 border-b border-white/8">
            <div className="h-9 w-9 rounded-sm bg-emerald-500/15 flex items-center justify-center"><Home className="h-4 w-4 text-emerald-400" /></div>
            <div className="text-sm font-medium text-slate-100">Property details</div>
          </div>

          <Field label="Property address" required>
            <input className={inputCls} required value={f.property_address} onChange={(e) => set("property_address", e.target.value)} data-testid="mf-address" />
          </Field>
          <AddressVisibilityNote />

          <div className="grid md:grid-cols-3 gap-4">
            <Field label="State" required>
              <select className={inputCls} required value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="mf-state">
                <option value="">Select state</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="County">
              <input className={inputCls} value={f.county} onChange={(e) => set("county", e.target.value)} data-testid="mf-county" />
            </Field>
            <Field label="Number of units" required>
              <input className={inputCls} type="number" min="1" required value={f.units} onChange={(e) => set("units", e.target.value)} data-testid="mf-units" />
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Asking price ($)" required>
              <input className={inputCls} type="number" min="0" required value={f.asking_price} onChange={(e) => set("asking_price", e.target.value)} data-testid="mf-asking" />
            </Field>
            <Field label="Current gross annual rents ($)">
              <input className={inputCls} type="number" min="0" value={f.annual_gross_rents} onChange={(e) => set("annual_gross_rents", e.target.value)} data-testid="mf-gross-rents" />
            </Field>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Occupancy (%)">
              <input className={inputCls} type="number" min="0" max="100" step="0.1" value={f.occupancy} onChange={(e) => set("occupancy", e.target.value)} data-testid="mf-occupancy" />
            </Field>
            <Field label="Cap rate (%)">
              <input className={inputCls} type="number" min="0" max="50" step="0.01" value={f.cap_rate} onChange={(e) => set("cap_rate", e.target.value)} data-testid="mf-cap" />
            </Field>
            <Field label="Year built">
              <input className={inputCls} type="number" min="1800" max="2030" value={f.year_built} onChange={(e) => set("year_built", e.target.value)} data-testid="mf-year" />
            </Field>
          </div>

          <Field label="Condition">
            <select className={inputCls} value={f.condition} onChange={(e) => set("condition", e.target.value)} data-testid="mf-condition">
              <option value="">Select condition</option>
              {CONDITION.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          <div className="pt-3 border-t border-white/8">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={f.seller_financing_available} onChange={(e) => set("seller_financing_available", e.target.checked)} data-testid="mf-sf-yes" />
              <span className="text-sm text-slate-200">Seller financing available</span>
            </label>
            {f.seller_financing_available && (
              <Field label="Terms (if known)">
                <textarea className={`${inputCls} mt-3`} rows={2} value={f.seller_financing_terms} onChange={(e) => set("seller_financing_terms", e.target.value)} data-testid="mf-sf-terms" />
              </Field>
            )}
          </div>

          <div className="pt-3 border-t border-white/8 grid md:grid-cols-2 gap-4">
            <Field label="Your name" required>
              <input className={inputCls} required value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="mf-contact-name" />
            </Field>
            <Field label="Phone">
              <input className={inputCls} value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="mf-contact-phone" />
            </Field>
          </div>

          <RoleAttestation
            role={f.contact_role}
            setRole={(v) => set("contact_role", v)}
            ownerAck={f.owner_attestation}
            setOwnerAck={(v) => set("owner_attestation", v)}
            repAck={f.representative_attestation}
            setRepAck={(v) => set("representative_attestation", v)}
            prefix="mf"
          />

          <div className="pt-2">
            <TimelineField value={f.timeline} onChange={(v) => set("timeline", v)} prefix="mf" />
          </div>

          <ReviewProcessNotice />

          <label className="flex items-start gap-3 p-3 rounded-sm border border-white/10 cursor-pointer">
            <input type="checkbox" required checked={f.listing_agreement_accepted} onChange={(e) => set("listing_agreement_accepted", e.target.checked)} className="mt-1" data-testid="mf-agreement" />
            <span className="text-xs text-slate-300">I confirm I have the right to list this property and that the information provided is accurate.</span>
          </label>

          <button type="submit" disabled={busy} data-testid="mf-submit" className="btn-primary w-full justify-center inline-flex items-center gap-2 disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? "Submitting..." : "Submit listing"}
          </button>
        </form>
      </SellerAuthGate>
    </PublicShell>
  );
}
